import { Suspense, useState, useCallback } from "react";
import GisterHeader from "../../components/Gister/GisterHeader";
import { useTenant } from "../../tenants/useTenant";
import useWizardEngine from "./useWizardEngine";
import { WizardProvider } from "./WizardContext";
import { useGister } from "./hooks/useGister";
import { isValidEmail, isValidPhone } from "../../tenants/gi/gister/forms/validators";
import styled from "styled-components";

const Gister = () => {
  const tenant = useTenant();
  const hospitalName = tenant.hospital.name.toLowerCase();
  const { submit, getHeaderText } = useGister({ hospitalName });

  const steps = tenant.gisterSteps;
  const wizard = useWizardEngine(steps);
  const StepComponent = wizard.step;
  
  // Trigger validation counter - incremented to force step components to validate
  const [triggerValidation, setTriggerValidation] = useState(0);
  const [doctorSelected, setDoctorSelected] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  
  const forceValidation = useCallback(() => {
    setTriggerValidation(v => v + 1);
  }, []);

  const onDoctorSelect = useCallback(() => {
    setDoctorSelected(true);
  }, []);

  return (
    <Suspense fallback={null}>
      <GisterHeader text={getHeaderText()} logoSrc={hospitalName === "gi" ? "/logoGI.svg" : undefined} />

      <WizardProvider
        value={{
          dataRef: wizard.dataRef,
          validateRef: wizard.validateRef,
          next: wizard.next,
          prev: wizard.prev,
          isFirst: wizard.isFirst,
          isLast: wizard.isLast,
          hospitalData: tenant.hospital,
          tenant,
          triggerValidation,
          forceValidation,
          onDoctorSelect
        }}
      >
        <StepComponent />

        {/* TODO - Seperate into another file */}
        {!wizard.isLast &&
          <SubmitButton
            onClick={() => wizard.next()}
          >
            Next
          </SubmitButton>
        }
        {wizard.isLast && doctorSelected &&
          <>
            <ConsentRow>
              <ConsentCheckbox
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                id="smsConsent"
              />
              <ConsentLabel htmlFor="smsConsent">
                Patient has consent to receive educational videos and reminders via SMS from UGI. Patient can unsubscribe at any time. Patient information is not shared with third parties or affiliates.
              </ConsentLabel>
            </ConsentRow>
            <SubmitButton
              disabled={!smsConsent}
              $disabled={!smsConsent}
              onClick={() => {
                const data = wizard.dataRef.current;
                const errors = {};
                
                // Validate Personal Information
                if ((data.zehutNumber || '').length < 4) errors.zehutNumber = true;
                const hasPhone = (data.phoneNumber || '').trim().length > 0;
                const hasEmail = (data.email || '').trim().length > 0;
                if (!hasPhone && !hasEmail) {
                  errors.contact = true;
                } else {
                  if (hasPhone && !isValidPhone(data.phoneNumber, data.phoneCode)) errors.contact = true;
                  if (hasEmail && !isValidEmail(data.email)) errors.contact = true;
                }
                
                // Validate Doctor
                if (!data.doctorId) errors.doctor = true;
                
                // Validate Procedure Details
                if (!data.date) errors.date = true;
                if (!data.time) errors.time = true;
                
                if (Object.keys(errors).length > 0) {
                  forceValidation();
                  return;
                }
                
                data.smsConsent = true;
                submit(data);
              }}
            >
              Send
            </SubmitButton>
          </>
        }
        {!wizard.isFirst &&
          <SubmitButton
            onClick={() => wizard.prev()}
          >
            Prev
          </SubmitButton>
        }
      </WizardProvider>

    </Suspense>
  );
};


export default Gister;


const SubmitButton = styled.button`
  display: block;
  margin-inline: auto;
  width: 7.8125vw;
  height: 4.35vh;
  border: 0;
  border-radius: 1.4vw;
  background-color: #F02A4C;
  color: #FFFFFF;
  font-size: 0.9895vw;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  margin-top: 20px;
  margin-bottom: 25px;
  font-family: 'Poppins', sans-serif;
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
`

const ConsentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5vw;
  margin-top: 10px;
  font-family: 'Poppins', sans-serif;
`

const ConsentCheckbox = styled.input`
  width: 1vw;
  height: 1vw;
  cursor: pointer;
  accent-color: #F02A4C;
`

const ConsentLabel = styled.label`
  max-width: 500px;
  font-size: 0.73vw;
  color: #444;
  cursor: pointer;
  user-select: none;
`